import React from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { MobileTool } from './MobileToolbar';
import PhotosPanel from '../sidebar/PhotosPanel';
import LayoutPanel from '../sidebar/LayoutPanel';
import BackgroundPanel from '../sidebar/BackgroundPanel';
import TextPanel from '../sidebar/TextPanel';
import StickersPanel from '../sidebar/StickersPanel';
import CoverPanel from '../sidebar/CoverPanel';
import { YearbookElement, YearbookPage } from '@/types/yearbook';
import { LayoutTemplateDef } from '@/lib/parseTemplateObjects';
import { TrameTemplateInfosByStyle, TrameBackgroundInfosByStyle, StickerInfosByStyle, CoverInfosByStyle, AllFolder } from '@/hooks/useBookLoader';

interface MobileToolDrawerProps {
  openTool: MobileTool | null;
  onClose: () => void;
  isCoverPage: boolean;
  isWememberz: boolean;

  onAddText: (variant?: 'title' | 'subtitle' | 'text', customContent?: string) => void;
  onAddImage: (url: string, ratio?: number, pictureId?: number, thumbUrl?: string) => void;
  onAddSticker: (imageUrl: string, width: number, height: number, opts?: { format?: 'png' | 'svg' | 'simple_svg'; defaultColor?: string }) => void;
  onAddShape: (shapeType: 'rect' | 'circle' | 'line', color?: string) => void;
  onSetBackground: (type: 'color' | 'image', value: string, backgroundTemplateId?: number, isCustom?: boolean) => void;
  onApplyLayout: (layout: LayoutTemplateDef, templateId?: number) => void;
  onApplyCover: (data: { elements_front: any[]; elements_back: any[]; cover_name?: string }) => void;
  activeTemplateId?: number | null;
  activeBackgroundId?: number | null;
  activeBackgroundCustomId?: number | null;

  selectedElement: YearbookElement | null;
  onUpdateElement: (id: string, updates: Partial<YearbookElement>) => void;

  trameTemplateInfos: TrameTemplateInfosByStyle;
  trameBackgroundInfos: TrameBackgroundInfosByStyle;
  stickerInfos: StickerInfosByStyle;
  coverInfos: CoverInfosByStyle;
  favColors: string[];
  onFavColorsChange: (colors: string[]) => void;
  pictureToUse: Set<number>;
  allFolders: AllFolder[];

  // Tracks which panels have ever been opened (lazy mount)
  mountedPanels: Set<MobileTool>;
}

const titles: Record<MobileTool, string> = {
  preview: 'Aperçu',
  cover: 'Couvertures',
  layout: 'Modèles',
  background: 'Fonds',
  text: 'Texte',
  stickers: 'Stickers',
  photos: 'Photos',
};

const MobileToolDrawer: React.FC<MobileToolDrawerProps> = (props) => {
  const {
    openTool, onClose, isCoverPage, isWememberz,
    onAddText, onAddImage, onAddSticker, onAddShape,
    onSetBackground, onApplyLayout, onApplyCover,
    activeTemplateId, activeBackgroundId, activeBackgroundCustomId,
    selectedElement, onUpdateElement,
    trameTemplateInfos, trameBackgroundInfos, stickerInfos, coverInfos,
    favColors, onFavColorsChange, pictureToUse, allFolders, mountedPanels,
  } = props;

  // We render *all* mounted panels and toggle visibility, so state is preserved
  // and API calls only fire on first open.
  const isMounted = (t: MobileTool) => mountedPanels.has(t);
  const isVisible = (t: MobileTool) => openTool === t;

  return (
    <Drawer open={openTool !== null && openTool !== 'preview'} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="h-[80vh] max-h-[80vh] focus:outline-none">
        <DrawerHeader className="py-2 px-4 border-b shrink-0">
          <DrawerTitle className="text-sm">{openTool ? titles[openTool] : ''}</DrawerTitle>
        </DrawerHeader>

        <div className="flex-1 min-h-0 overflow-hidden bg-editor-sidebar">
          {/* photos */}
          {isMounted('photos') && (
            <div className={`h-full overflow-y-auto p-3 ${isVisible('photos') ? '' : 'hidden'}`}>
              <PhotosPanel
                onAddImage={(url, ratio, pictureId, thumbUrl) => {
                  onAddImage(url, ratio, pictureId, thumbUrl);
                  onClose();
                }}
                onAddText={onAddText}
                pictureToUse={pictureToUse}
                allFolders={allFolders}
                isActive={isVisible('photos')}
              />
            </div>
          )}

          {/* layout */}
          {isMounted('layout') && !isCoverPage && (
            <div className={`h-full overflow-y-auto p-3 ${isVisible('layout') ? '' : 'hidden'}`}>
              <LayoutPanel
                onApplyLayout={(l, id) => { onApplyLayout(l, id); onClose(); }}
                activeTemplateId={activeTemplateId}
                trameTemplateInfos={trameTemplateInfos}
                isWememberz={isWememberz}
              />
            </div>
          )}

          {/* cover */}
          {isMounted('cover') && isCoverPage && (
            <div className={`h-full overflow-y-auto p-3 ${isVisible('cover') ? '' : 'hidden'}`}>
              <CoverPanel
                onApplyCover={(data) => { onApplyCover(data); onClose(); }}
                coverInfos={coverInfos}
                isWememberz={isWememberz}
              />
            </div>
          )}

          {/* background */}
          {isMounted('background') && (
            <div className={`h-full overflow-y-auto p-3 ${isVisible('background') ? '' : 'hidden'}`}>
              <BackgroundPanel
                onSetBackground={(type, val, id, isCustom) => {
                  onSetBackground(type, val, id, isCustom);
                  // Use setTimeout to ensure the drawer closes after state update
                  setTimeout(() => onClose(), 50);
                }}
                activeBackgroundId={activeBackgroundId}
                activeBackgroundCustomId={activeBackgroundCustomId}
                trameBackgroundInfos={trameBackgroundInfos}
                favColors={favColors}
                onFavColorsChange={onFavColorsChange}
                isWememberz={isWememberz}
              />
            </div>
          )}

          {/* text */}
          {isMounted('text') && (
            <div className={`h-full overflow-y-auto p-3 ${isVisible('text') ? '' : 'hidden'}`}>
              <TextPanel
                onAddText={(v) => { onAddText(v); onClose(); }}
                selectedElement={selectedElement}
                onUpdateElement={onUpdateElement}
                favColors={favColors}
                onFavColorsChange={onFavColorsChange}
              />
            </div>
          )}

          {/* stickers */}
          {isMounted('stickers') && (
            <div className={`h-full overflow-y-auto p-3 ${isVisible('stickers') ? '' : 'hidden'}`}>
              <StickersPanel
                onAddSticker={(url, w, h, opts) => { onAddSticker(url, w, h, opts); onClose(); }}
                onAddShape={(t, c) => { onAddShape(t, c); onClose(); }}
                stickerInfos={stickerInfos}
                selectedElement={selectedElement}
                onUpdateElement={onUpdateElement}
                favColors={favColors}
                onFavColorsChange={onFavColorsChange}
              />
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileToolDrawer;